/**
 * createTableMutations.ts - Binds the plain mutation functions in this directory into the
 * {@link TableMutationMethods} bundle that `TableModelProvider` puts on the `TableModel`.
 *
 * Every method reads fresh state through `params.getTableModel()` and `params.getTableProps()` at
 * call time, so nothing in the
 * bundle is cell-specific and one bundle is shared by every consumer within a table.
 */

import assert from 'assert';

import * as MultiSelectListUtil from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {SelectionRestoreData} from 'notebook/mediocre/selection/SelectionStore';
import type {Cell, FormatPropertyChange, PastedCell} from 'notebook/tables/constants/CellConstants';
import {
  type ColumnConstraint,
  type InsertableColumnInfo,
} from 'notebook/tables/constants/TableColumnConstants';
import type {TableSetting} from 'notebook/tables/constants/TableConstants';
import {MultiSelectionBox, TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {type NamedTableOperation} from 'notebook/tables/excess/ops/TableOpConstants';
import {ReorderOptions} from 'notebook/tables/excess/ops/TableOps';
import type {TableOpContextInterface} from 'notebook/tables/spreadsheets/TableOpContext';
import {
  applySettingToSelection,
  replaceCells,
} from 'notebook/tables/store/logic/mutations/applySettingToSelection';
import {clearCellsInRange, clearSelection} from 'notebook/tables/store/logic/mutations/clearCellsInRange';
import {canCutSelection, copySelection, cutSelection} from 'notebook/tables/store/logic/mutations/clipboard';
import {
  deleteColumns,
  insertColumnsLeft,
  insertColumnsRight,
  renameColumn,
  setColumnConstraint,
} from 'notebook/tables/store/logic/mutations/columnOps';
import {
  resizeColumns,
  setColumnWidth,
  setColumnWidths,
} from 'notebook/tables/store/logic/mutations/columnWidths';
import {deleteTable} from 'notebook/tables/store/logic/mutations/deleteTable';
import {expandToCell} from 'notebook/tables/store/logic/mutations/expandToCell';
import {performDoubleClickFillDown, performFillDown} from 'notebook/tables/store/logic/mutations/fillDown';
import {
  type FinalizeMultiEditOptions,
  finalizeMultiEdit,
} from 'notebook/tables/store/logic/mutations/finalizeMultiEditedCell';
import {
  type FreezeColumnsOptions,
  type FreezeColumnsResult,
  freezeThroughColumn,
  unfreezeFromColumn,
} from 'notebook/tables/store/logic/mutations/freezeColumns';
import {hideHoverCell, showHoverCell} from 'notebook/tables/store/logic/mutations/hoverCell';
import {type TableMutationParams} from 'notebook/tables/store/logic/mutations/mutationParams';
import {pasteTable, type PasteTableOptions} from 'notebook/tables/store/logic/mutations/pasteTable';
import {reorderRowsOrColumns} from 'notebook/tables/store/logic/mutations/reorderRowsOrColumns';
import {
  addBlankRows,
  deleteRows,
  insertRowsAbove,
  insertRowsBelow,
} from 'notebook/tables/store/logic/mutations/rowOps';
import {saveSelection, setMultiIndices} from 'notebook/tables/store/logic/mutations/selection';
import {
  setSelectionProperties,
  setSelectionSelector,
} from 'notebook/tables/store/logic/mutations/selectionProperties';
import {toggleHeaderCol, toggleHeaderRow} from 'notebook/tables/store/logic/mutations/toggleHeader';
import {updateCell} from 'notebook/tables/store/logic/mutations/updateCell';
import {type MergeValidationResult, validateMerge} from 'notebook/tables/store/logic/mutations/validateMerge';
import {type TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';

interface TableOpsMethods {
  renameTable: (tableName: string, newTableName: string) => void;
  performTableOp?: (tableOp: NamedTableOperation) => void;
  deleteRowsNonContiguous: (tableName: string, ranges: Array<[number, number]>) => void;
  mergeRegion: ((tableName: string, selectedBox: MultiSelectionBox) => void) | null;
  unmergeRegion: ((tableName: string, selectedBox: MultiSelectionBox) => void) | null;
  /**
   * Reorder rows/columns of the table via the underlying table op. This is the raw
   * op used to reorder single-input columns; drag-to-reorder of regular rows/columns
   * goes through {@link TableMutationMethods.reorderRowsOrColumns}.
   */
  reorderSingleInputs: (tableName: string, options: ReorderOptions) => void;
}

export interface TableMutationMethods extends TableOpsMethods {
  /**
   * Update a single cell in the table.
   *
   * @param cellIndex - The [row, column] index of the cell to update
   * @param cellData - Partial cell data to merge into the existing cell
   * @param expandTable - If true, add a new blank row at the bottom
   */
  updateCell: (cellIndex: TableIndex, cellData: Partial<Cell>, expandTable?: boolean) => void;

  /**
   * Commit the multi-window edit of the cell at `index` from the editor's `formula` text and
   * `typedValue` (a non-empty typed value wins and clears the text).
   *
   * No-op when the cell is unchanged. Otherwise checkpoints the current selection for undo, runs
   * `options.beforePersist`, persists the cell with an `update-cells` op, and tracks the containers
   * removed from the old cell.
   */
  finalizeMultiEdit: (
    index: TableIndex,
    formula: string,
    typedValue: TypedValue | null,
    options?: FinalizeMultiEditOptions
  ) => void;

  /**
   * Clear all cells within a selection box.
   *
   * @param selectionBox - The box defining which cells to clear
   */
  clearCellsInRange: (selectionBox: MultiSelectionBox) => void;

  /**
   * Clear all cells within the current selection, expanding to fully contain any
   * intersecting merged regions.
   *
   * Checkpoints the current selection for undo, clears the cells, and leaves cell
   * editing. No-op when there is no real selection.
   */
  clearSelection: () => void;

  /**
   * Delete a contiguous range of rows.
   *
   * Checkpoints the deleted rows as the selection for undo, then deletes them. `end`
   * defaults to `start`; no-op when `start` is null.
   */
  deleteRows: (start: number | null, end: number | null) => void;

  /**
   * Insert as many rows above the given range as the range spans.
   *
   * Checkpoints the range as the selection for undo before inserting. `end` defaults
   * to `start`; no-op when `start` is null.
   */
  insertRowsAbove: (start: number | null, end: number | null) => void;

  /**
   * Insert as many rows below the given range as the range spans.
   *
   * Checkpoints the range as the selection for undo before inserting. `end` defaults
   * to `start`; no-op when `start` is null.
   */
  insertRowsBelow: (start: number | null, end: number | null) => void;

  /**
   * Append `numRows` blank rows to the end of the table, limited to the rows the table can still
   * add (warning when the count is reduced), and select the new last row.
   *
   * Returns false without changing anything when the table cannot add any more rows.
   */
  addBlankRows: (numRows: number) => boolean;

  /**
   * Grow the table until it contains `index` (typically a ghost cell) and return the cell now at
   * that index.
   *
   * Checkpoints `index` as the selection for undo before persisting. Must not be called on a table
   * with locked dimensions.
   */
  expandToCell: (index: TableIndex) => Cell;

  /**
   * Delete a contiguous range of columns.
   *
   * Checkpoints the deleted columns as the selection for undo, then deletes them. `end` defaults to
   * `start`; no-op when `start` is null or when the table does not allow columns to be added and
   * removed.
   */
  deleteColumns: (start: number | null, end: number | null) => void;

  /**
   * Insert as many columns to the left of the given range as the range spans.
   *
   * Checkpoints the range as the selection for undo before inserting. `end` defaults to `start`;
   * no-op when `start` is null or when the table does not allow columns to be added and removed.
   */
  insertColumnsLeft: (
    start: number | null,
    end: number | null,
    columnInfo?: InsertableColumnInfo | {}
  ) => void;

  /**
   * Insert as many columns to the right of the given range as the range spans.
   *
   * Checkpoints the range as the selection for undo before inserting. `end` defaults to `start`;
   * no-op when `start` is null or when the table does not allow columns to be added and removed.
   */
  insertColumnsRight: (
    start: number | null,
    end: number | null,
    columnInfo?: InsertableColumnInfo | {}
  ) => void;

  /**
   * Rename the column at `index`, checkpointing the current selection for undo first.
   */
  renameColumn: (index: number, name: string) => void;

  /**
   * Set the constraint of the column at `index`, optionally merging `headerRowCell` into that
   * column's header cell, checkpointing the current selection for undo first.
   */
  setColumnConstraint: (
    index: number,
    columnConstraint: ColumnConstraint,
    headerRowCell?: Partial<Cell>
  ) => void;

  /**
   * Replace the table's column widths, checkpointing the current selection for undo first.
   */
  setColumnWidths: (widths: Array<number>) => void;

  /**
   * Set the width of the column at `columnIndex`. When that column lies within the selected column
   * range, every column in the range is resized.
   */
  resizeColumns: (columnIndex: number, width: number) => void;

  /**
   * Set the width of only the column at `columnIndex`, regardless of the current selection.
   */
  setColumnWidth: (columnIndex: number, width: number) => void;

  /**
   * Freeze every column from the single-input boundary through column `j`.
   *
   * Returns `noop` when that many columns are already frozen and `merge-conflict` when a merged
   * cell crosses the new boundary; the caller surfaces any toast. Otherwise checkpoints the
   * selection for undo, runs `options.beforePersist`, and persists the new frozen column count.
   */
  freezeThroughColumn: (j: number, options?: FreezeColumnsOptions) => FreezeColumnsResult;

  /**
   * Unfreeze column `j` and every frozen column after it. Same result and option semantics as
   * {@link freezeThroughColumn}.
   */
  unfreezeFromColumn: (j: number, options?: FreezeColumnsOptions) => FreezeColumnsResult;

  /**
   * Set or delete (when the value is `null`) cell properties on every cell in the merge-expanded
   * selection, checkpointing the current selection for undo first. Evaluable properties re-evaluate
   * the touched cells; other properties persist with a `noop` op.
   */
  setSelectionProperties: (updates: Array<FormatPropertyChange>) => void;

  /**
   * Set (or clear, when `staticId` is `null`) the selector on every selected cell.
   */
  setSelectionSelector: (staticId: string | null) => void;

  /**
   * Replace every cell of the table and re-evaluate it with an `eval-table` op.
   */
  replaceCells: (newCells: Array<Array<Cell>>) => void;

  /**
   * Run a table setting against the merge-expanded selection; the cells it reports through
   * `onUpdateCells` are persisted with {@link replaceCells}. Requires a real selection.
   */
  applySettingToSelection: (applySetting: TableSetting['applySettingToSelection']) => void;

  /**
   * Delete the whole table via the embedding surface's delete handler.
   *
   * Only valid when {@link TableModel.canDeleteTable} is true; callers gate on it.
   */
  deleteTable: () => void;

  /**
   * Toggle the header column flag on the table, clearing bold formatting on the first column.
   */
  toggleHeaderCol: () => void;

  /**
   * Toggle the header row flag on the table, clearing bold formatting on the first row.
   */
  toggleHeaderRow: () => void;

  /**
   * Validate whether the current selection can be merged into a single cell.
   *
   * Reads the merge-expanded selection box from the model and reports whether the
   * selection straddles the freeze boundary (merging disallowed) and whether the
   * merge would discard cell values (caller should confirm). Returns `null` when
   * there is no active selection.
   *
   * The merge itself is performed separately via `mergeRegion`; the confirmation
   * modal is owned by the component.
   */
  validateMerge: () => MergeValidationResult | null;

  /**
   * Paste a 2D array of cells into the table.
   *
   * Orchestrates the full paste pipeline (excluding clipboard parsing and file
   * attachment, which remain in the component): resolves the destination
   * selection, tiles/reshapes the pasted cells to fill it, validates the paste
   * (surfacing warnings/errors via notifications), normalizes typed values,
   * asynchronously resolves selectors, writes the cells via the pure
   * `pasteTable` mutation, enforces capacity limits, remerges single-input
   * columns, persists the change, and tracks analytics.
   *
   * @param cells - The 2D array of cells to paste (already parsed from the clipboard).
   * @param options - Paste options: `transpose` and the originating `selectionIndex`.
   * @param setSelection - Component callback to update the multi-selection. Called
   *   once with the expanded paste region (so a rejected paste is visible) and again
   *   with the final pasted region.
   */
  pasteTable: (
    cells: Array<Array<PastedCell>>,
    options: PasteTableOptions,
    setSelection: (args: {start: TableIndex; end: TableIndex; text?: string}) => void
  ) => void;

  /**
   * Set the table's multi-selection anchor (`start`) and end indices.
   *
   * Writes the selection indices to the store and syncs the spreadsheet editing
   * state for the anchor cell. When `text` is omitted, the anchor cell's current
   * text / typed value are read from the table.
   *
   * Does NOT move DOM focus — the caller is responsible for focusing the
   * focus-bearer element afterward when needed.
   *
   * @param indices - The `start` (anchor) and `end` indices, plus optional `text`
   *   to seed the editing state for the anchor cell
   * @param options - Optional `multiSelIndex` to keep as the selected index;
   *   defaults to the `start` anchor when omitted
   */
  setMultiIndices: (
    indices: {start: TableIndex; end: TableIndex; text?: string},
    options?: {multiSelIndex?: TableIndex | null}
  ) => void;

  /**
   * Checkpoint the current selection onto the undo/redo restore history.
   *
   * Snapshots the current multi-selection state (or an explicit override) and
   * pushes it via the table's `storeState` handler. Typically called before a
   * structural edit so the prior selection can be restored on undo.
   *
   * @param override - Optional selection state to snapshot instead of the
   *   current store state
   */
  saveSelection: (
    override?: Partial<MultiSelectListUtil.MultiSelectState<TableIndex>> | {selection: SelectionRestoreData}
  ) => void;

  /**
   * Complete a drag-to-reorder of rows or columns.
   *
   * Encapsulates the full `onDragComplete` flow: reads the in-progress drag
   * dimension and multi-selection from the model, clears the dragging state,
   * rejects (surfacing an error notification) when the drop position would split
   * a merged cell, checkpoints the selection for undo via `saveSelection`,
   * adjusts the frozen-column count for column reorders, performs the reorder via
   * the `reorderSingleInputs` op (skipped when the drop is a no-op), updates the
   * selection to the reordered range via `setMultiIndices`, and tracks analytics.
   *
   * Does NOT move DOM focus — the caller is responsible for focusing the
   * focus-bearer element afterward.
   *
   * @param insertIndex - The index the dragged range is dropped before.
   */
  reorderRowsOrColumns: (insertIndex: number) => void;

  /**
   * Fill down the current selection into the active fill-down box.
   *
   * Checks fill-down locking (surfacing a warning notification and aborting when
   * locked), reads the source selection (`expandedBox`) and destination
   * (`fillDownBox`) from the models, checkpoints the current selection for undo
   * via `saveSelection`, extrapolates via the pure `fillDown` mutation,
   * persists the change, and updates the selection to the filled region via
   * `setMultiIndices`. No-op when there is no active selection or fill-down box.
   */
  performFillDown: () => void;

  /**
   * Fill down the current selection through the following blank rows, triggered
   * by double-clicking the fill handle.
   *
   * Reads the source selection (`expandedBox`) and the double-click destination
   * (`fillDown.doubleClickBox`) from the models, extrapolates via the pure
   * `doubleClickFillDown` mutation, persists the change, and updates the
   * selection to the filled region via `setMultiIndices`. No-op when the
   * selection contains a merged region.
   */
  performDoubleClickFillDown: () => void;

  /**
   * Hide the hover affordances on the cell under the pointer, so they do not sit
   * under the pointer while the user types. No-op when already hidden.
   */
  hideHoverCell: () => void;

  /**
   * Show the hover affordances on the cell under the pointer again, on the next
   * pointer movement. No-op when already shown.
   */
  showHoverCell: () => void;

  /**
   * Copy the current selection to the clipboard as plain text and as an HTML table, warning when
   * the clipboard write fails.
   */
  copySelection: () => Promise<void>;

  /**
   * Copy the current selection to the clipboard, then clear it once the copy succeeds. Callers gate
   * on {@link canCutSelection}.
   */
  cutSelection: () => Promise<void>;

  /**
   * Whether the current selection may be cut: it contains no cell locked on the table itself.
   * Read when the action is offered (e.g. when the cell context menu opens).
   */
  canCutSelection: () => boolean;
}

/**
 * Binds `params` into each mutation method. `disableMergingCells` nulls out the merge ops so
 * consumers can hide merge affordances.
 */
export function createTableMutations(
  params: TableMutationParams,
  disableMergingCells: boolean | undefined
): TableMutationMethods {
  const {tableOps} = params;
  return {
    updateCell: (cellIndex, cellData, expandTable) => {
      updateCell(params, cellIndex, cellData, expandTable);
    },
    finalizeMultiEdit: (index, formula, typedValue, options) => {
      finalizeMultiEdit(params, index, formula, typedValue, options);
    },
    clearCellsInRange: (selectionBox) => {
      clearCellsInRange(params, selectionBox);
    },
    clearSelection: () => {
      clearSelection(params);
    },
    deleteRows: (start, end) => {
      deleteRows(params, start, end);
    },
    insertRowsAbove: (start, end) => {
      insertRowsAbove(params, start, end);
    },
    insertRowsBelow: (start, end) => {
      insertRowsBelow(params, start, end);
    },
    addBlankRows: (numRows) => addBlankRows(params, numRows),
    expandToCell: (index) => expandToCell(params, index),
    deleteColumns: (start, end) => {
      deleteColumns(params, start, end);
    },
    insertColumnsLeft: (start, end, columnInfo) => {
      insertColumnsLeft(params, start, end, columnInfo);
    },
    insertColumnsRight: (start, end, columnInfo) => {
      insertColumnsRight(params, start, end, columnInfo);
    },
    renameColumn: (index, name) => {
      renameColumn(params, index, name);
    },
    setColumnConstraint: (index, columnConstraint, headerRowCell) => {
      setColumnConstraint(params, index, columnConstraint, headerRowCell);
    },
    setColumnWidths: (widths) => {
      setColumnWidths(params, widths);
    },
    resizeColumns: (columnIndex, width) => {
      resizeColumns(params, columnIndex, width);
    },
    setColumnWidth: (columnIndex, width) => {
      setColumnWidth(params, columnIndex, width);
    },
    freezeThroughColumn: (j, options) => freezeThroughColumn(params, j, options),
    unfreezeFromColumn: (j, options) => unfreezeFromColumn(params, j, options),
    setSelectionProperties: (updates) => {
      setSelectionProperties(params, updates);
    },
    setSelectionSelector: (staticId) => {
      setSelectionSelector(params, staticId);
    },
    replaceCells: (newCells) => {
      replaceCells(params, newCells);
    },
    applySettingToSelection: (applySetting) => {
      applySettingToSelection(params, applySetting);
    },
    deleteTable: () => {
      deleteTable(params);
    },
    toggleHeaderCol: () => {
      toggleHeaderCol(params);
    },
    toggleHeaderRow: () => {
      toggleHeaderRow(params);
    },
    validateMerge: () => validateMerge(params),
    pasteTable: (cells, options, setSelection) => {
      pasteTable(params, cells, options, setSelection);
    },
    setMultiIndices: (indices, options) => {
      setMultiIndices(params, indices, options);
    },
    saveSelection: (override) => {
      saveSelection(params, override);
    },
    reorderRowsOrColumns: (insertIndex) => {
      reorderRowsOrColumns(params, insertIndex);
    },
    performFillDown: () => {
      performFillDown(params);
    },
    performDoubleClickFillDown: () => {
      performDoubleClickFillDown(params);
    },
    hideHoverCell: () => {
      hideHoverCell(params);
    },
    showHoverCell: () => {
      showHoverCell(params);
    },
    copySelection: () => copySelection(params),
    cutSelection: () => cutSelection(params),
    canCutSelection: () => canCutSelection(params),
    renameTable: tableOps.renameTable,
    performTableOp: tableOps.performTableOp,
    deleteRowsNonContiguous: tableOps.deleteTableRowsNonContiguous,
    mergeRegion: disableMergingCells ? null : tableOps.mergeTableRegion,
    unmergeRegion: disableMergingCells ? null : tableOps.unmergeTableRegion,
    reorderSingleInputs: tableOps.reorderTableRowsOrColumns,
  };
}

function assertTableMutationsUnavailable(): never {
  assert(false, 'Table mutations require a TableModelProvider mounted within a TableOpContextProvider');
}

const UNAVAILABLE_TABLE_OPS: TableOpContextInterface = {
  performTableOp: assertTableMutationsUnavailable,
  deleteTableRowsNonContiguous: assertTableMutationsUnavailable,
  deleteTableRows: assertTableMutationsUnavailable,
  deleteTableColumns: assertTableMutationsUnavailable,
  insertTableRowsAbove: assertTableMutationsUnavailable,
  insertTableRowsBelow: assertTableMutationsUnavailable,
  reorderTableRowsOrColumns: assertTableMutationsUnavailable,
  insertTableColumnsLeft: assertTableMutationsUnavailable,
  insertTableColumnsRight: assertTableMutationsUnavailable,
  renameTable: assertTableMutationsUnavailable,
  mergeTableRegion: assertTableMutationsUnavailable,
  unmergeTableRegion: assertTableMutationsUnavailable,
  mergeIntoSingleInput: assertTableMutationsUnavailable,
  unmergeSingleInput: assertTableMutationsUnavailable,
};

/**
 * The mutations of a model that has no table to mutate: axis cells rendered outside any table, and
 * tables mounted without a `TableOpContextProvider` (isolated harnesses). Every method asserts when
 * called, and merging is reported as unavailable.
 */
export const UNAVAILABLE_TABLE_MUTATIONS: TableMutationMethods = createTableMutations(
  {
    getTableModel: assertTableMutationsUnavailable,
    getTableProps: assertTableMutationsUnavailable,
    setState: assertTableMutationsUnavailable,
    tableNoteContext: null,
    tableOps: UNAVAILABLE_TABLE_OPS,
  },
  true
);
